/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Backoffice Navigation Sidebar (Task 11.1.3 / Task 11.1.4 / Task 4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { ExternalLink } from 'lucide-react';
import { SophiaIcon, type SophiaDefaultIconName } from '@sofia/assets';
import { useI18n } from '../../i18n';
import { useTheme } from '../../context/ThemeProvider';
import { getAdminDomains } from '../../lib/urls';
import { resolveViewToPath } from '../../routes/admin-routes';

export type AdminViewId =
  | 'dashboard'
  | 'aiModels'
  | 'aiSandbox'
  | 'keypool'
  | 'plans'
  | 'gateways'
  | 'orders'
  | 'orderLogs'
  | 'financial'
  | 'credits'
  | 'users'
  | 'cmsBlog'
  | 'cmsPages'
  | 'cmsNavigation'
  | 'settings'
  | 'themeStudio'
  | 'templateStudio'
  | 'brandStudio'
  | 'seedAssets'
  | 'tokenAnalytics'
  | 'auditLogs';

export interface AdminNavItem {
  id: AdminViewId;
  labelKey: string;
  icon: React.FC<{ className?: string }>;
  sophiaIcon: SophiaDefaultIconName;
  section: 'overview' | 'ai' | 'billing' | 'management' | 'studio' | 'system';
}

export interface AdminNavSection {
  id: 'overview' | 'ai' | 'billing' | 'management' | 'studio' | 'system';
  labelKey: string;
  sophiaIcon: SophiaDefaultIconName;
  itemIds: AdminViewId[];
}

export const ADMIN_NAV_SECTIONS: AdminNavSection[] = [
  {
    id: 'overview',
    labelKey: 'admin.nav.sectionOverview',
    sophiaIcon: 'strategy',
    itemIds: ['dashboard', 'tokenAnalytics', 'financial'],
  },
  {
    id: 'ai',
    labelKey: 'admin.nav.sectionAi',
    sophiaIcon: 'processor',
    itemIds: ['aiModels', 'aiSandbox', 'keypool'],
  },
  {
    id: 'billing',
    labelKey: 'admin.nav.sectionBilling',
    sophiaIcon: 'digital-wallet',
    itemIds: ['plans', 'gateways', 'orders', 'orderLogs', 'credits'],
  },
  {
    id: 'management',
    labelKey: 'admin.nav.sectionManagement',
    sophiaIcon: 'city-space',
    itemIds: ['users', 'cmsBlog', 'cmsPages', 'cmsNavigation'],
  },
  {
    id: 'studio',
    labelKey: 'admin.nav.sectionStudio',
    sophiaIcon: 'hologram',
    itemIds: ['themeStudio', 'templateStudio', 'brandStudio', 'seedAssets'],
  },
  {
    id: 'system',
    labelKey: 'admin.nav.sectionSystem',
    sophiaIcon: 'server',
    itemIds: ['settings', 'auditLogs'],
  },
];

export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  {
    id: 'dashboard',
    labelKey: 'admin.nav.dashboard',
    icon: (props) => <SophiaIcon name="structure-server" size={16} {...props} />,
    sophiaIcon: 'structure-server',
    section: 'overview',
  },
  {
    id: 'aiModels',
    labelKey: 'admin.nav.aiModels',
    icon: (props) => <SophiaIcon name="processor" size={16} {...props} />,
    sophiaIcon: 'processor',
    section: 'ai',
  },
  {
    id: 'aiSandbox',
    labelKey: 'admin.nav.aiSandbox',
    icon: (props) => <SophiaIcon name="flask" size={16} {...props} />,
    sophiaIcon: 'flask',
    section: 'ai',
  },
  {
    id: 'keypool',
    labelKey: 'admin.nav.keypool',
    icon: (props) => <SophiaIcon name="finger-scanner" size={16} {...props} />,
    sophiaIcon: 'finger-scanner',
    section: 'ai',
  },
  {
    id: 'plans',
    labelKey: 'admin.nav.plans',
    icon: (props) => <SophiaIcon name="digital-wallet" size={16} {...props} />,
    sophiaIcon: 'digital-wallet',
    section: 'billing',
  },
  {
    id: 'gateways',
    labelKey: 'admin.nav.gateways',
    icon: (props) => <SophiaIcon name="solution" size={16} {...props} />,
    sophiaIcon: 'solution',
    section: 'billing',
  },
  {
    id: 'orders',
    labelKey: 'admin.nav.orders',
    icon: (props) => <SophiaIcon name="digital-wallet" size={16} {...props} />,
    sophiaIcon: 'digital-wallet',
    section: 'billing',
  },
  {
    id: 'orderLogs',
    labelKey: 'admin.nav.orderLogs',
    icon: (props) => <SophiaIcon name="big-data" size={16} {...props} />,
    sophiaIcon: 'big-data',
    section: 'billing',
  },
  {
    id: 'financial',
    labelKey: 'admin.nav.financial',
    icon: (props) => <SophiaIcon name="strategy" size={16} {...props} />,
    sophiaIcon: 'strategy',
    section: 'overview',
  },
  {
    id: 'credits',
    labelKey: 'admin.nav.credits',
    icon: (props) => <SophiaIcon name="battery" size={16} {...props} />,
    sophiaIcon: 'battery',
    section: 'billing',
  },
  {
    id: 'users',
    labelKey: 'admin.nav.users',
    icon: (props) => <SophiaIcon name="city-space" size={16} {...props} />,
    sophiaIcon: 'city-space',
    section: 'management',
  },
  {
    id: 'cmsBlog',
    labelKey: 'admin.nav.cmsBlog',
    icon: (props) => <SophiaIcon name="brainstorm" size={16} {...props} />,
    sophiaIcon: 'brainstorm',
    section: 'management',
  },
  {
    id: 'cmsPages',
    labelKey: 'admin.nav.cmsPages',
    icon: (props) => <SophiaIcon name="layer" size={16} {...props} />,
    sophiaIcon: 'layer',
    section: 'management',
  },
  {
    id: 'cmsNavigation',
    labelKey: 'admin.nav.cmsNavigation',
    icon: (props) => <SophiaIcon name="network" size={16} {...props} />,
    sophiaIcon: 'network',
    section: 'management',
  },
  {
    id: 'settings',
    labelKey: 'admin.nav.settings',
    icon: (props) => <SophiaIcon name="server" size={16} {...props} />,
    sophiaIcon: 'server',
    section: 'system',
  },
  {
    id: 'themeStudio',
    labelKey: 'admin.nav.themeStudio',
    icon: (props) => <SophiaIcon name="hologram" size={16} {...props} />,
    sophiaIcon: 'hologram',
    section: 'studio',
  },
  {
    id: 'templateStudio',
    labelKey: 'admin.nav.templateStudio',
    icon: (props) => <SophiaIcon name="city-space" size={16} {...props} />,
    sophiaIcon: 'city-space',
    section: 'studio',
  },
  {
    id: 'brandStudio',
    labelKey: 'admin.nav.brandStudio',
    icon: (props) => <SophiaIcon name="smart-glasses" size={16} {...props} />,
    sophiaIcon: 'smart-glasses',
    section: 'studio',
  },
  {
    id: 'seedAssets',
    labelKey: 'admin.nav.seedAssets',
    icon: (props) => <SophiaIcon name="big-data" size={16} {...props} />,
    sophiaIcon: 'big-data',
    section: 'studio',
  },
  {
    id: 'tokenAnalytics',
    labelKey: 'admin.nav.tokenAnalytics',
    icon: (props) => <SophiaIcon name="cpu" size={16} {...props} />,
    sophiaIcon: 'cpu',
    section: 'overview',
  },
  {
    id: 'auditLogs',
    labelKey: 'admin.nav.auditLogs',
    icon: (props) => <SophiaIcon name="finger-scanner" size={16} {...props} />,
    sophiaIcon: 'finger-scanner',
    section: 'system',
  },
];

export interface AdminSidebarProps {
  activeView: AdminViewId;
  onViewChange: (view: AdminViewId) => void;
  className?: string;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  activeView,
  onViewChange,
  className = '',
}) => {
  const { t } = useI18n();
  const { branding, isDark } = useTheme();
  const domains = getAdminDomains();

  const logoUrl = isDark ? (branding?.logoDarkUrl || branding?.logoLightUrl) : (branding?.logoLightUrl || branding?.logoDarkUrl);

  return (
    <aside className={`w-64 flex flex-col border-r border-surface-border bg-surface select-none ${className}`}>
      {/* Brand Header */}
      <div className="h-16 flex items-center gap-3 px-5 border-b border-surface-border">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={branding?.siteName || 'Sophia Admin'}
            className="h-8 max-w-[120px] object-contain"
            onError={(e) => {
              (e.currentTarget as HTMLElement).style.display = 'none';
              const fb = e.currentTarget.nextElementSibling as HTMLElement;
              if (fb) fb.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className="w-8 h-8 rounded-lg bg-gradient-to-tr from-primary to-accent flex items-center justify-center text-white font-bold text-sm shadow-glow-primary shrink-0"
          style={logoUrl ? { display: 'none' } : undefined}
        >
          <SophiaIcon name="structure-server" size={18} className="text-white" />
        </div>
        <div className="flex flex-col overflow-hidden">
          <span className="font-heading font-bold text-sm tracking-tight text-foreground truncate max-w-[130px]">
            {branding?.siteName || 'Sophia Mission'}
          </span>
          <span className="text-[10px] text-muted-fg font-mono uppercase tracking-wider">
            Super Admin Edge
          </span>
        </div>
      </div>

      {/* Navigation List grouped by Section */}
      <div className="flex-1 py-4 px-3 space-y-4 overflow-y-auto">
        {ADMIN_NAV_SECTIONS.map((section) => {
          const sectionItems = ADMIN_NAV_ITEMS.filter((item) => item.section === section.id);
          if (sectionItems.length === 0) return null;

          return (
            <div key={section.id} className="space-y-1">
              <div className="flex items-center gap-2 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-fg/80">
                <SophiaIcon name={section.sophiaIcon} size={12} className="text-muted-fg/60 shrink-0" />
                <span>{t(section.labelKey)}</span>
              </div>

              {sectionItems.map((item) => {
                const isActive = activeView === item.id;
                const itemPath = resolveViewToPath(item.id);

                return (
                  <a
                    key={item.id}
                    href={itemPath}
                    onClick={(e) => {
                      if (!e.metaKey && !e.ctrlKey && e.button === 0) {
                        e.preventDefault();
                        onViewChange(item.id);
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium transition-colors group ${
                      isActive
                        ? 'bg-primary/10 text-primary font-semibold shadow-sm'
                        : 'text-muted-fg hover:text-foreground hover:bg-surface-elevated'
                    }`}
                  >
                    <SophiaIcon
                      name={item.sophiaIcon}
                      size={16}
                      className={`shrink-0 transition-colors ${isActive ? 'text-primary' : 'text-muted-fg group-hover:text-foreground'}`}
                    />
                    <span className="truncate">{t(item.labelKey)}</span>
                  </a>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Quick Link to User Workspace & Site */}
      <div className="p-3 border-t border-surface-border space-y-1 bg-surface-elevated/40">
        <a
          href={domains.appOrigin}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-between px-3 py-2 text-[11px] font-medium text-muted-fg hover:text-foreground hover:bg-surface rounded-lg transition-colors group"
        >
          <div className="flex items-center gap-2">
            <SophiaIcon name="brainstorm" size={13} className="text-muted-fg group-hover:text-foreground shrink-0" />
            <span>User Workspace</span>
          </div>
          <ExternalLink className="w-3 h-3 text-muted-fg group-hover:text-foreground" />
        </a>
        <a
          href={domains.siteOrigin}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-between px-3 py-2 text-[11px] font-medium text-muted-fg hover:text-foreground hover:bg-surface rounded-lg transition-colors group"
        >
          <div className="flex items-center gap-2">
            <SophiaIcon name="hologram" size={13} className="text-muted-fg group-hover:text-foreground shrink-0" />
            <span>Landing Page</span>
          </div>
          <ExternalLink className="w-3 h-3 text-muted-fg group-hover:text-foreground" />
        </a>
      </div>
    </aside>
  );
};
